import { reactive, computed } from 'vue'
import SiteAdminService from '@/services/SiteAdminService'
import { trackSites, SITES_EVENTS } from '@/lib/tracking/sites'

/**
 * Giving configuration of one site (castr `GET /sites/{id}/giving`,
 * sites-broadcast-template-contract.md §5), shared by every component that
 * depends on it: the shell's navigation (Monetize › Donations appears with
 * native giving), Settings › Broadcast (the Giving card edits it) and the
 * Overview (giving card). One load per site; a save replaces the cache.
 */
const states = new Map()

function stateFor(siteId) {
	if (!states.has(siteId)) {
		states.set(siteId, reactive({
			giving: null,
			loaded: false,
			loading: false,
			error: null,
		}))
	}
	return states.get(siteId)
}

export function useSiteGiving(siteId) {
	const id = () => (typeof siteId === 'function' ? siteId() : siteId?.value ?? siteId)
	const state = computed(() => stateFor(id()))

	const giving = computed(() => state.value.giving)
	// Giving is `off` or `native`; a castr that still reports the retired `external` mode means off.
	const mode = computed(() => (giving.value?.mode === 'native' ? 'native' : 'off'))
	const isNative = computed(() => mode.value === 'native')
	// Why native giving would not work right now, as castr reports it
	// (`donations_disabled` kill switch, `paywall_donations_disabled`,
	// `paywall_unavailable`) — null when fine or unknown. Nothing hides on it.
	const nativeUnavailable = computed(() => giving.value?.nativeUnavailable ?? null)

	/** Load once (or again with `force`); errors are kept on `state.error`, never thrown. */
	async function load({ force = false } = {}) {
		const s = state.value
		if (s.loading || (s.loaded && !force)) return s.giving
		s.loading = true
		try {
			s.giving = await SiteAdminService.getGiving(id())
			s.error = null
		} catch (err) {
			s.error = err
		}
		s.loaded = true
		s.loading = false
		return s.giving
	}

	/** `PUT /sites/{id}/giving`; the answer (the GET shape) replaces the cache. Throws on failure. */
	async function save(body) {
		const wasNative = isNative.value
		const res = await SiteAdminService.updateGiving(id(), body)
		if (!wasNative && res?.mode === 'native') trackSites(SITES_EVENTS.GIVING_ENABLED, id(), { mode: res.mode })
		const s = state.value
		s.giving = res
		s.loaded = true
		s.error = null
		return res
	}

	return { state, giving, mode, isNative, nativeUnavailable, load, save }
}
